import type {
    CacheType,
    CoreQuestionReportingFilters,
    CoreQuestionReportingResponse,
    DomainsResponse,
    GeneralReportingResponse,
    SurveyReportingFilters,
    SurveyReportingResponse,
    VotingActivityFilters,
    VotingActivityResponse,
} from '@dmp/qqms/types';
import { ApiUriBuilder, downloadUrl, fatch } from '@dmp/shared/client-utils';
import type { DeepPartial } from '@dmp/shared/types';
import type {
    AuthGroupCreateAttributes,
    AuthGroupUpdateAttributes,
    CoreQuestionCreateAttributes,
    CoreQuestionUpdateAttributes,
    CoreQuestionsUpdateAttributes,
    SurveyCreateAttributes,
    SurveyDuplicateAttributes,
    SurveyUpdateAttributes,
    TeamCreateAttributes,
    TeamUpdateAttributes,
} from './api-requests';
import type {
    AssetResource,
    AuthGroupListResponse,
    AuthGroupResponse,
    CoreQuestionListResponse,
    CoreQuestionResponse,
    SerializedAuthenticatedUser,
    SurveyItemResponse,
    SurveyListResponse,
    SurveyLiveProofResponse,
    SurveyPublishResponse,
    TeamListResponse,
    TeamResponse,
} from './api-responses';
import type { SurveysQuery } from './api-validators';

const apiUriBuilder = new ApiUriBuilder();

const buildReportingQueryString = (
    filters: SurveyReportingFilters | CoreQuestionReportingFilters
) => {
    const queryParams = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
            queryParams.append(key, String(value));
        }
    });

    return queryParams.toString();
};

// ------------------------------------------------
// Surveys
// ------------------------------------------------

export type SurveyListQuery = Omit<SurveysQuery, 'filter'> & {
    filter?: Omit<NonNullable<SurveysQuery['filter']>, 'publicationStatus'> & {
        publicationStatus?: string;
    };
};

export const fetchSurveys = (query: DeepPartial<SurveyListQuery>) => {
    const teamId = query.filter?.teamId;

    return fatch<SurveyListResponse>(
        apiUriBuilder.resolve<SurveyListQuery>(`teams/${teamId}/surveys`, {
            ...query,
            include: ['createdBy', 'updatedBy'],
            sort: [
                {
                    field: 'updatedAt',
                    direction: 'DESC',
                },
            ],
        })
    );
};

export const fetchSurvey = (surveyId: string) =>
    fatch<SurveyItemResponse>(apiUriBuilder.resolve(`surveys/${surveyId}`));

export const fetchSurveyReporting = (
    surveyId: string,
    filters: SurveyReportingFilters
) => {
    return fatch<SurveyReportingResponse>(
        apiUriBuilder.resolve(
            `reporting/surveys/${surveyId}?${buildReportingQueryString(
                filters
            )}`
        )
    );
};

export const fetchVotingActivity = (
    surveyId: string,
    filters: VotingActivityFilters
) =>
    fatch<VotingActivityResponse>(
        apiUriBuilder.resolve(
            `reporting/surveys/${surveyId}/voting-activity?${buildReportingQueryString(
                filters
            )}`
        )
    );

export const fetchGeneralReporting = (filters: SurveyReportingFilters) => {
    return fatch<GeneralReportingResponse>(
        apiUriBuilder.resolve(
            `reporting/general?${buildReportingQueryString(filters)}`
        )
    );
};

export const downloadReportingCsv = (filters: SurveyReportingFilters) => {
    return downloadUrl(
        apiUriBuilder.resolve(
            `reporting/export?${buildReportingQueryString({
                ...filters,
            })}`
        )
    );
};

export const fetchCoreQuestionReporting = (
    filters: CoreQuestionReportingFilters
) => {
    return fatch<CoreQuestionReportingResponse>(
        apiUriBuilder.resolve(
            `reporting/core-questions?${buildReportingQueryString(filters)}`
        )
    );
};

export const downloadCoreQuestionReportingCsv = (
    filters: CoreQuestionReportingFilters
) => {
    return downloadUrl(
        apiUriBuilder.resolve(
            `reporting/core-questions/export?${buildReportingQueryString(
                filters
            )}`
        )
    );
};

export const findAllDomains = () => {
    return fatch<DomainsResponse>(apiUriBuilder.resolve(`domains`));
};

export const createSurvey = (payload: SurveyCreateAttributes) =>
    fatch<SurveyItemResponse>(apiUriBuilder.resolve('surveys'), {
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        body: JSON.stringify({ ...payload, name: payload.name.trim() }),
    });

export const duplicateSurvey = (
    surveyId: string,
    payload: SurveyDuplicateAttributes
) =>
    fatch<SurveyItemResponse>(
        apiUriBuilder.resolve(`surveys/${surveyId}/duplicate`),
        {
            headers: { 'Content-Type': 'application/json' },
            method: 'POST',
            body: JSON.stringify({
                newSurveyName: payload.newSurveyName.trim(),
            }),
        }
    );

export const patchSurvey = (
    surveyId: string,
    payload: SurveyUpdateAttributes
) =>
    fatch(apiUriBuilder.resolve(`surveys/${surveyId}`), {
        headers: { 'Content-Type': 'application/json' },
        method: 'PATCH',
        body: JSON.stringify(payload),
    });

export const publishSurvey = (surveyId: string) =>
    fatch<SurveyPublishResponse>(
        apiUriBuilder.resolve(`surveys/${surveyId}/publish`)
    );

export const publishSurveyProof = (surveyId: string) =>
    fatch<SurveyLiveProofResponse>(
        apiUriBuilder.resolve(`surveys/${surveyId}/live-proof`)
    );

// ------------------------------------------------
// Users
// ------------------------------------------------

export const fetchCurrentUser = () =>
    fatch<SerializedAuthenticatedUser>(apiUriBuilder.resolve('users/current'));

// ------------------------------------------------
// Core Questions
// ------------------------------------------------

export const fetchCoreQuestionList = () =>
    fatch<CoreQuestionListResponse>(
        apiUriBuilder.resolve(`core-questions`, {
            sort: [{ field: 'updatedAt', direction: 'DESC' }],
        })
    );

export const patchCoreQuestion = (
    coreQuestionId: string,
    payload: CoreQuestionUpdateAttributes
) =>
    fatch<CoreQuestionResponse>(
        apiUriBuilder.resolve(`core-questions/${coreQuestionId}`),
        {
            headers: { 'Content-Type': 'application/json' },
            method: 'PATCH',
            body: JSON.stringify(payload),
        }
    );

/**
 * Update multiple Core Questions.
 */
export const patchCoreQuestions = (payload: CoreQuestionsUpdateAttributes) =>
    fatch<CoreQuestionListResponse>(apiUriBuilder.resolve('core-questions'), {
        headers: { 'Content-Type': 'application/json' },
        method: 'PATCH',
        body: JSON.stringify(payload),
    });

export const postCoreQuestion = (payload: CoreQuestionCreateAttributes) =>
    fatch<CoreQuestionResponse>(apiUriBuilder.resolve('core-questions'), {
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        body: JSON.stringify(payload),
    });

// ------------------------------------------------
// Upload file/images
// ------------------------------------------------

export const uploadImage = (surveyId: string, file: File) => {
    const payload = new FormData();
    payload.append('file', file);

    return fatch<AssetResource>(
        apiUriBuilder.resolve(`surveys/${surveyId}/sponsor-logo`),
        {
            method: 'PATCH',
            body: payload,
        }
    );
};

// ------------------------------------------------
// Auth Groups
// ------------------------------------------------

export const fetchAuthGroups = () =>
    fatch<AuthGroupListResponse>(
        apiUriBuilder.resolve('auth-groups', {
            sort: [
                {
                    field: 'name',
                    direction: 'ASC',
                },
            ],
            include: ['teams'],
        })
    );

export const createAuthGroup = (payload: AuthGroupCreateAttributes) =>
    fatch<AuthGroupResponse>(apiUriBuilder.resolve('auth-groups'), {
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        body: JSON.stringify(payload),
    });

export const updateAuthGroup = (
    authGroupId: string,
    payload: AuthGroupUpdateAttributes
) =>
    fatch<AuthGroupResponse>(
        apiUriBuilder.resolve(`auth-groups/${authGroupId}`),
        {
            headers: { 'Content-Type': 'application/json' },
            method: 'PATCH',
            body: JSON.stringify(payload),
        }
    );

export const deleteAuthGroup = (authGroupId: string) =>
    fatch<void>(apiUriBuilder.resolve(`auth-groups/${authGroupId}`), {
        method: 'DELETE',
    });

export const addTeamToAuthGroup = (authGroupId: string, teamId: string) =>
    fatch<AuthGroupResponse>(
        apiUriBuilder.resolve(`auth-groups/${authGroupId}/teams/${teamId}`),
        {
            headers: { 'Content-Type': 'application/json' },
            method: 'PUT',
            body: '',
        }
    );

export const removeTeamFromAuthGroup = (authGroupId: string, teamId: string) =>
    fatch<AuthGroupResponse>(
        apiUriBuilder.resolve(`auth-groups/${authGroupId}/teams/${teamId}`),
        {
            headers: { 'Content-Type': 'application/json' },
            method: 'DELETE',
            body: '',
        }
    );

// ------------------------------------------------
// Teams
// ------------------------------------------------

export const fetchTeamList = () =>
    fatch<TeamListResponse>(
        apiUriBuilder.resolve('teams', {
            sort: [
                {
                    field: 'updatedAt',
                    direction: 'ASC',
                },
            ],
        })
    );

export const createTeam = (payload: TeamCreateAttributes) =>
    fatch<TeamResponse>(apiUriBuilder.resolve('teams'), {
        headers: { 'Content-Type': 'application/json' },
        method: 'POST',
        body: JSON.stringify(payload),
    });

export const updateTeam = (teamId: string, payload: TeamUpdateAttributes) =>
    fatch<TeamResponse>(apiUriBuilder.resolve(`teams/${teamId}`), {
        headers: { 'Content-Type': 'application/json' },
        method: 'PATCH',
        body: JSON.stringify(payload),
    });

// ------------------------------------------------
// Cache
// ------------------------------------------------

export const clearAllCaches = () =>
    fatch(apiUriBuilder.resolve('cache'), {
        method: 'DELETE',
    });

export const clearCacheType = (cacheType: CacheType) =>
    fatch(apiUriBuilder.resolve(`cache/${cacheType}`), {
        method: 'DELETE',
    });
